import { g as getLogger } from './logger.js';
import EventEmitter from 'eventemitter3';
import { C as CoreEvent, b as PlayingChangeType, P as PlaybackState } from './event.js';
import { DestroyableBase, DestroyableSet, destroyableEventListener, destroyableTimeout } from '@nrk/destroyable';
import { t as toError } from './error.js';
import { S as SessionClosedReason } from './event-handler.js';
import { t as toMediaTypeString, p as parseMediaTypeString, g as guessMediaTypeOfUrl, a as isAudioOnlyMediaType } from './media-type.js';

/**
 * The main exports are the CoreCustomEventEmitter class and the
 * CoreCustomEventeventTarget interface. Use the event emitter to emit core
 * events as custom events on an EventTarget of choice (for example through the
 * EmitProxy). Use the CoreCustomEventTarget interface to apply typed
 * addEventListener and removeEventListener methods to the same target. For
 * example:
 *
 *    const element = document.createElement('div');
 *    const emitter = new CoreCustomEventEmitter(element);
 *
 *    (element as CoreCustomEventTarget).addEventListener(
 *      CoreEvent.Playing,
 *      (event: CoreCustomEvent<CoreEvent.Playing>) => {
 *        // The event type is an alias for CustomEvent<PlayingEvent>.
 *      }
 *    );
 */
// The detail types for the core custom events, based on the handler map.
// According to spec?

/**
 * CustomEvent with the correct detail type for the specified CoreEvent(s).
 */

/**
 * An event listener for core custom events.
 */

/**
 * An event listener object for core custom events.
 */

/**
 * Interface for applying the core custom event and listener types to
 * addEventListener and removeEventListener on an event target, either by
 * casting or by custom elements implementing the interface.
 */

/**
 * Emits core events as custom events on an event target.
 */
class CoreCustomEventEmitter {
  constructor(eventTarget) {
    this.eventTarget = void 0;
    this.eventTarget = eventTarget;
  }

  emit(type, ...args) {
    // Handling of args.length === 0 removed because it's currently always 1.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const customEvent = new CustomEvent(type, {
      bubbles: true,
      detail: args[0]
    });
    this.eventTarget.dispatchEvent(customEvent);
  }

}

const logger$9 = getLogger('CoreEvent');
/**
 * Typed wrapper class for event emitter.
 */

class CoreEventEmitter {
  constructor() {
    this.emitter = new EventEmitter();
  }

  on(event, handler, context) {
    this.emitter.on(event, handler, context);
  }

  once(event, handler, context) {
    this.emitter.once(event, handler, context);
  }

  off(event, handler, context) {
    this.emitter.off(event, handler, context);
  }

  emit(event, ...args) {
    this.emitter.emit(event, ...args);
  }

  reset() {
    this.emitter.removeAllListeners();
  }

}
class MultiEmitEventEmitter extends CoreEventEmitter {
  constructor(emitters) {
    super();
    this.emitters = void 0;
    this.emitters = emitters;
  }

  emit(event, ...args) {
    // Somewhat frequent events can be on debug level.
    if (event === CoreEvent.MediaRangeChange || event === CoreEvent.SubtitlesCueUpdate) {
      logger$9.debug('Emitting event:', event, ...args);
    } // Don't log time update events. They're too frequent.
    else if (event !== CoreEvent.TimeUpdate && event !== CoreEvent.ScrubberUpdate) {
      logger$9.log('Emitting event:', event, ...args);
    }

    super.emit(event, ...args);
    this.emitters.forEach(emitter => {
      // @ts-expect-error - Problematic signature matching.
      emitter.emit(event, ...args);
    });
  }

  reset() {
    super.reset();
    this.emitters.forEach(emitter => emitter instanceof CoreEventEmitter && emitter.reset());
  }

}

const logger$8 = getLogger('ScrubberUpdate'); // How often to emit scrubber update events during live streams. Set the
// default to the standard for HTML5 timeupdate events: At least 4 times per
// second.

const LIVE_SCRUBBER_UPDATE_INTERVAL = 250;
/**
 * Applies ScrubberUpdate events to a session.
 */

class ScrubberUpdateEvents {
  constructor(session, eventEmitter, interval) {
    this.session = void 0;
    this.eventEmitter = void 0;
    this.interval = void 0;
    this.intervalId = void 0;
    this.session = session;
    this.eventEmitter = eventEmitter;
    this.interval = interval != null ? interval : LIVE_SCRUBBER_UPDATE_INTERVAL;

    if (session.isTimeRunning()) {
      this.start();
    } else {
      eventEmitter.once(CoreEvent.TimeRunningChange, () => this.start());
    }

    eventEmitter.on(CoreEvent.Broken, () => this.destroy());
    eventEmitter.on(CoreEvent.SessionClosed, () => this.destroy());
  }

  start() {
    if (this.session.isLiveStream() && !this.session.isLiveStreamEnded()) {
      logger$8.log('Emitting scrubber update events by interval timer.');
      this.eventEmitter.emit(CoreEvent.ScrubberUpdate, this.session.getScrubberValues());
      this.intervalId = setInterval(() => {
        this.eventEmitter.emit(CoreEvent.ScrubberUpdate, this.session.getScrubberValues());
      }, this.interval); // When live streams end, change to piggyback on the TimeUpdate events,
      // as the ScrubberRange is now fixed and equal to the media range.

      this.eventEmitter.once(CoreEvent.LiveStreamEnded, () => {
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = undefined;
        }

        logger$8.log('Changing to piggybacking on time updates.');
        this.eventEmitter.on(CoreEvent.TimeUpdate, () => {
          this.eventEmitter.emit(CoreEvent.ScrubberUpdate, this.session.getScrubberValues());
        });
      });
    } else {
      // Emit ScrubberUpdate events for on-demand streams.
      logger$8.log('Emitting scrubber update events by piggybacking on time updates.');
      this.eventEmitter.on(CoreEvent.TimeUpdate, () => {
        this.eventEmitter.emit(CoreEvent.ScrubberUpdate, this.session.getScrubberValues());
      });
    }
  }

  destroy() {
    // Don't bother removing all the event listeners. They will be removed when
    // the event emitter is reset.
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

}

/**
 * Points to an adapter class and the configuration (constructor parameters
 * after the first required adapter context parameter provided elsewhere) it
 * should get when instantiated.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
class ConfiguredAdapter {
  constructor(adapterClass, ...config) {
    this.adapterClass = void 0;
    this.adapterConfig = void 0;
    this.adapterClass = adapterClass;
    this.adapterConfig = config;
  }

}

/* eslint-disable @typescript-eslint/no-empty-function */

/**
 * For when display modes are not supported.
 */
class NullDisplayModes {
  getFullscreenState() {
    return {
      isSupported: false,
      isAvailable: false,
      isActive: false,
      isNativeUI: false
    };
  }

  enterFullscreen() {}

  exitFullscreen() {}

  getPiPState() {
    return {
      isSupported: false,
      isAvailable: false,
      isActive: false
    };
  }

  enterPiP() {}

  exitPiP() {}

  getRemotePlaybackState() {
    return {
      isSupported: false,
      isAvailable: false,
      isActive: false,
      isScanning: false
    };
  }

  startRemoteTargetScan() {}

  stopRemoteTargetScan() {}

  enterRemotePlayback() {}

  destroy() {}

}

const logger$7 = getLogger('AdapterSnapshot');
/**
 * Read-only/no-op adapter which takes a snapshot of another adapter.
 */

class AdapterSnapshot extends DestroyableBase {
  constructor(values) {
    super();
    this.values = void 0;
    this.values = values;
  }
  /**
   * Grab all values from another adapter.
   */


  static from(other) {
    return new AdapterSnapshot({
      adapterInfo: other.getAdapterInfo(),
      mediaInfo: other.getMediaInfo(),
      state: other.getState(),
      waitingReason: other.getWaitingReason(),
      pausedReason: other.getPausedReason(),
      error: other.getError(),
      liveStream: other.isLiveStream(),
      liveStreamEnded: other.isLiveStreamEnded(),
      seekable: other.isSeekable(),
      currentTime: other.getCurrentTime(),
      currentTimeLive: other.getCurrentTimeLive(),
      programDateTimeAtPosZero: other.positionToLiveTime(0).getTime(),
      scrubberValues: other.getScrubberValues(),
      playbackRate: other.getPlaybackRate(),
      muted: other.isMuted(),
      volume: other.getVolume(),
      mediaRange: other.getMediaRange(),
      scrubberRange: other.getScrubberRange(),
      seekableRange: other.getSeekableRange()
    });
  }

  getAdapterInfo() {
    logger$7.log('Getting adapter info from adapter snapshot.');
    return this.values.adapterInfo;
  }

  getMediaInfo() {
    logger$7.log('Getting media info from adapter snapshot.');
    return this.values.mediaInfo;
  }

  preload() {
    logger$7.log('Calling preload on adapter snapshot (no-op).');
    return Promise.resolve();
  }

  createTimeRangeEventTrack() {
    throw new Error("Can't create time range event track on adapter snapshot.");
  }

  play() {
    logger$7.log('Calling play on adapter snapshot (no-op).');
    return Promise.resolve();
  }

  pause() {
    logger$7.log('Calling pause on adapter snapshot (no-op).');
  }

  getState() {
    logger$7.log('Getting state from adapter snapshot.');
    return this.values.state;
  }

  getWaitingReason() {
    logger$7.log('Getting waiting state from adapter snapshot.');
    return this.values.waitingReason;
  }

  getPausedReason() {
    logger$7.log('Getting paused state from adapter snapshot.');
    return this.values.pausedReason;
  }

  getError() {
    logger$7.log('Getting error from adapter snapshot.');
    return this.values.error;
  }

  isLiveStream() {
    logger$7.log('Getting live stream from adapter snapshot.');
    return this.values.liveStream;
  }

  isLiveStreamEnded() {
    logger$7.log('Getting live stream ended from adapter snapshot.');
    return this.values.liveStreamEnded;
  }

  isSeekable() {
    logger$7.log('Getting seekable from adapter snapshot.');
    return this.values.seekable;
  }

  seekTo() {
    logger$7.log('Seeking on adapter snapshot (no-op).');
  }

  seekToRelativeTime() {
    logger$7.log('Seeking on adapter snapshot (no-op).');
  }

  seekToLive() {
    logger$7.log('Seeking on adapter snapshot (no-op).');
  }

  seekToLiveTime() {
    logger$7.log('Seeking on adapter snapshot (no-op).');
  }

  getCurrentTime() {
    logger$7.log('Getting current time from adapter snapshot.');
    return this.values.currentTime;
  }

  getCurrentTimeLive() {
    logger$7.log('Getting current live time from adapter snapshot.');
    return this.values.currentTimeLive;
  } // Actually supported.


  positionToLiveTime(position) {
    return new Date((this.values.programDateTimeAtPosZero + position) * 1000);
  } // Actually supported.


  liveTimeToPosition(liveTime) {
    return liveTime.getTime() / 1000 - this.values.programDateTimeAtPosZero;
  }

  getScrubberValues() {
    logger$7.log('Getting scrubber values from adapter snapshot.');
    return this.values.scrubberValues;
  }

  setPlaybackRate() {
    logger$7.log('Setting playback rate on adapter snapshot (no-op).');
  }

  getPlaybackRate() {
    logger$7.log('Getting playback rate from adapter snapshot.');
    return this.values.playbackRate;
  }

  resetPlaybackRate() {
    logger$7.log('Resetting playback rate on adapter snapshot (no-op).');
  }

  mute() {
    logger$7.log('Muting on adapter snapshot (no-op).');
  }

  unmute() {
    logger$7.log('Unmuting on adapter snapshot (no-op).');
  }

  isMuted() {
    logger$7.log('Getting muted from adapter snapshot.');
    return this.values.muted;
  }

  setVolume() {
    logger$7.log('Setting volume on adapter snapshot (no-op).');
  }

  getVolume() {
    logger$7.log('Getting volume from adapter snapshot.');
    return this.values.volume;
  }

  getMediaRange() {
    logger$7.log('Getting media range from adapter snapshot.');
    return this.values.mediaRange;
  }

  getScrubberRange() {
    logger$7.log('Getting scrubber range from adapter snapshot.');
    return this.values.scrubberRange;
  }

  getSeekableRange() {
    logger$7.log('Getting seekable range from adapter snapshot.');
    return this.values.seekableRange;
  }

  addSubtitlesTrack() {
    throw new Error("Can't add subtitles to the adapter snapshot.");
  }

  setNoSubtitles() {
    logger$7.log('Setting no subtitles on adapter snapshot (no-op).');
  }

  setDefaultSubtitles() {
    logger$7.log('Setting default subtitles on adapter snapshot (no-op).');
  }

  setAutoSubtitles() {
    logger$7.log('Setting auto subtitles on adapter snapshot (no-op).');
  }

  setActiveSubtitles() {
    logger$7.log('Setting active subtitles on adapter snapshot (no-op).');
  }

  getActiveSubtitles() {
    logger$7.log('Getting active subtitles from adapter snapshot.');
    return undefined;
  }

  getAvailableSubtitles() {
    logger$7.log('Getting available subtitles menu from adapter snapshot.');
    return [];
  }

  enableNativeSubtitlesDisplay() {
    logger$7.log('Enabling native subtitles display on adapter snapshot (no-op).');
  }

  disableNativeSubtitlesDisplay() {
    logger$7.log('Disabling native subtitles display on adapter snapshot (no-op).');
  }

  isNativeSubtitlesDisplayEnabled() {
    logger$7.log('Getting subtitles display enabled from adapter snapshot.');
    return false;
  }

  destroy() {
    super.destroy();
    return Promise.resolve();
  }

}

const logger$6 = getLogger('PlaybackSession');
/**
 * A session has a state and functionality for playing one piece of media.
 *
 * A new session is created for every media. Sessions should be closed when
 * they're no longer needed, for example when changing to the next media in a
 * playlist.
 *
 * If you're listening to events on a session, you'll only ever get events for
 * the media that is associated with that session. There's no need to worry
 * about if or when media changes. Use the {@link CoreEvent.SessionCreated}
 * event for attaching listeners to new sessions.
 *
 * When a session is closed, its state is still available through all the "get"
 * methods, but the action methods stop working.
 */

class PlaybackSession {
  // "Double" private to shield from TS and JS users.
  // Events.
  // Adapter.
  // Playback controls (implemented by adapter).
  // Subtitle controls (implemented by adapter).
  // Display modes.
  constructor(adapter, eventEmitter, externalEventEmitter, qualityTracker, displayModes) {
    this.__adapter = void 0;
    this.__eventEmitter = void 0;
    this.__qualityTracker = void 0;
    this.__displayModes = void 0;
    this.on = void 0;
    this.off = void 0;
    this.once = void 0;
    this.getAdapterInfo = void 0;
    this.getMediaInfo = void 0;
    this.preload = void 0;
    this.createTimeRangeEventTrack = void 0;
    this.play = void 0;
    this.pause = void 0;
    this.getState = void 0;
    this.getWaitingReason = void 0;
    this.getPausedReason = void 0;
    this.getError = void 0;
    this.isLiveStream = void 0;
    this.isLiveStreamEnded = void 0;
    this.isSeekable = void 0;
    this.seekTo = void 0;
    this.seekToRelativeTime = void 0;
    this.seekToLive = void 0;
    this.seekToLiveTime = void 0;
    this.getCurrentTime = void 0;
    this.getCurrentTimeLive = void 0;
    this.positionToLiveTime = void 0;
    this.liveTimeToPosition = void 0;
    this.getScrubberValues = void 0;
    this.setPlaybackRate = void 0;
    this.getPlaybackRate = void 0;
    this.resetPlaybackRate = void 0;
    this.mute = void 0;
    this.unmute = void 0;
    this.isMuted = void 0;
    this.setVolume = void 0;
    this.getVolume = void 0;
    this.getMediaRange = void 0;
    this.getScrubberRange = void 0;
    this.getSeekableRange = void 0;
    this.addSubtitlesTrack = void 0;
    this.setNoSubtitles = void 0;
    this.setDefaultSubtitles = void 0;
    this.setAutoSubtitles = void 0;
    this.setActiveSubtitles = void 0;
    this.getActiveSubtitles = void 0;
    this.getAvailableSubtitles = void 0;
    this.enableNativeSubtitlesDisplay = void 0;
    this.disableNativeSubtitlesDisplay = void 0;
    this.isNativeSubtitlesDisplayEnabled = void 0;
    this.getFullscreenState = void 0;
    this.enterFullscreen = void 0;
    this.exitFullscreen = void 0;
    this.getPiPState = void 0;
    this.enterPiP = void 0;
    this.exitPiP = void 0;
    this.getRemotePlaybackState = void 0;
    this.startRemoteTargetScan = void 0;
    this.stopRemoteTargetScan = void 0;
    this.enterRemotePlayback = void 0;
    this.__adapter = adapter;
    this.__eventEmitter = eventEmitter;
    this.__qualityTracker = qualityTracker;
    this.__displayModes = displayModes; // Create all the session methods.

    const emitterMethods = ['on', 'off', 'once'];
    const adapterMethods = ['getAdapterInfo', 'getMediaInfo', 'preload', 'createTimeRangeEventTrack', 'play', 'pause', 'getState', 'getWaitingReason', 'getPausedReason', 'getError', 'isLiveStream', 'isLiveStreamEnded', 'isSeekable', 'seekTo', 'seekToRelativeTime', 'seekToLive', 'seekToLiveTime', 'getCurrentTime', 'setPlaybackRate', 'getPlaybackRate', 'resetPlaybackRate', 'mute', 'unmute', 'isMuted', 'setVolume', 'getVolume', 'getMediaRange', 'getScrubberValues', 'getScrubberRange', 'getSeekableRange', 'addSubtitlesTrack', 'setNoSubtitles', 'setDefaultSubtitles', 'setAutoSubtitles', 'setActiveSubtitles', 'getActiveSubtitles', 'getAvailableSubtitles', 'enableNativeSubtitlesDisplay', 'disableNativeSubtitlesDisplay', 'isNativeSubtitlesDisplayEnabled', 'getCurrentTimeLive', 'positionToLiveTime', 'liveTimeToPosition'];
    const displayModeMethods = ['getFullscreenState', 'enterFullscreen', 'exitFullscreen', 'getPiPState', 'enterPiP', 'exitPiP', 'getRemotePlaybackState', 'startRemoteTargetScan', 'stopRemoteTargetScan', 'enterRemotePlayback'];
    emitterMethods.forEach(method => {
      Object.defineProperty(this, method, {
        value: externalEventEmitter[method].bind(externalEventEmitter)
      });
    });
    adapterMethods.forEach(method => {
      Object.defineProperty(this, method, {
        // @ts-expect-error - Length of args.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        value: (...args) => this.__adapter[method](...args)
      });
    });
    displayModeMethods.forEach(method => {
      Object.defineProperty(this, method, {
        value: displayModes[method].bind(displayModes)
      });
    }); // Make sure there's a QualityChange event emitted at replay.

    eventEmitter.on(CoreEvent.PlayingChange, event => {
      if (event.type === PlayingChangeType.Replay) {
        const dispatch = () => qualityTracker.dispatchQualityChangeEvent();

        eventEmitter.once(CoreEvent.TimeRunningChange, dispatch);
        eventEmitter.once(CoreEvent.QualityChange, () => {
          eventEmitter.off(CoreEvent.TimeRunningChange, dispatch);
        });
      }
    }); // Adapter closing events.

    eventEmitter.once(CoreEvent.Broken, () => {
      this.close(SessionClosedReason.Broken).catch(err => {
        logger$6.error('Failed to close session:', err);
      });
    });
    adapter.addEventListener('destroy', () => {
      this.close(adapter.destroyedReason).catch(err => {
        logger$6.error('Failed to close session:', err);
      });
    }); // Ready.

    eventEmitter.emit(CoreEvent.SessionCreated, {
      session: this
    });
  } // Convenience methods, only using information from the adapter.

  /**
   * Returns true if playback has not been attempted yet.
   *
   * @category Convenience
   */


  isIdle() {
    return this.getState() === PlaybackState.Idle;
  }
  /**
   * Returns true if playback is happening or attempted.
   *
   * @category Convenience
   */


  isPlaying() {
    return this.getState() === PlaybackState.Playing;
  }
  /**
   * Returns true if playback is attempted but time is not running for some
   * reason, or if seeking while paused.
   *
   * @category Convenience
   */


  isWaiting() {
    return this.getWaitingReason() != undefined;
  }
  /**
   * Returns true if the player is actually playing.
   *
   * @category Convenience
   */


  isTimeRunning() {
    return this.isPlaying() && !this.isWaiting();
  }
  /**
   * Returns true if the player is in the broken state.
   *
   * @category Convenience
   */


  isBroken() {
    return this.getState() === PlaybackState.Broken;
  } // Other methods.

  /**
   * Returns the current quality of a variable bitrate stream.
   */


  getCurrentQuality() {
    return this.__qualityTracker.getCurrentQuality();
  }
  /**
   * Close the session, release resources and drop event listeners. The state
   * of the session is still available, but all action methods are no-ops.
   */


  async close(reason) {
    // Guard against multiple calls to close.
    if (this.__adapter instanceof AdapterSnapshot) {
      return;
    }

    logger$6.log('Closing session. Reason:', reason); // Replace the adapter with a snapshot.

    const adapter = this.__adapter;
    this.__adapter = AdapterSnapshot.from(adapter);

    if (!adapter.destroyed) {
      await adapter.destroy();
    }

    this.__displayModes.destroy();

    this.__eventEmitter.emit(CoreEvent.SessionClosed, {
      session: this,
      reason
    });

    this.__eventEmitter.reset();
  }

}

/* eslint-disable @typescript-eslint/no-non-null-assertion */
const logger$5 = getLogger('Fullscreen');

function getSupportedIntegration() {
  /* eslint-disable @typescript-eslint/explicit-function-return-type */
  const integrations = [{
    type: 'standard',
    changeEvent: 'fullscreenchange',
    isEnabled: () => document.fullscreenEnabled,
    requestFullscreen: element => element.requestFullscreen(),
    exitFullscreen: () => document.exitFullscreen(),
    getActiveElement: () => document.fullscreenElement || undefined
  }, {
    type: 'webkit',
    changeEvent: 'webkitfullscreenchange',
    isEnabled: () => document.webkitFullscreenEnabled,
    requestFullscreen: element => element.webkitRequestFullscreen(),
    exitFullscreen: () => document.webkitExitFullscreen(),
    getActiveElement: () => document.webkitFullscreenElement
  }, {
    type: 'moz',
    changeEvent: 'mozfullscreenchange',
    isEnabled: () => document.mozFullScreenEnabled,
    requestFullscreen: element => element.mozRequestFullScreen(),
    exitFullscreen: () => document.mozCancelFullScreen(),
    getActiveElement: () => document.mozFullScreenElement
  }, {
    type: 'ms',
    changeEvent: 'MSFullscreenChange',
    isEnabled: () => document.msFullscreenEnabled,
    requestFullscreen: element => element.msRequestFullscreen(),
    exitFullscreen: () => document.msExitFullscreen(),
    getActiveElement: () => document.msFullscreenElement
  }];
  /* eslint-enable @typescript-eslint/explicit-function-return-type */

  return integrations.find(integration => integration.isEnabled());
}

class Fullscreen extends DestroyableSet {
  constructor(element, stateChangedCallback) {
    var _window$screen$orient;

    super();
    this.element = void 0;
    this.stateChangedCallback = void 0;
    this.integration = void 0;
    this.supportsOrientationLock = void 0;
    this.isActive = false;
    const integration = getSupportedIntegration();

    if (!integration) {
      throw new Error('Fullscreen not supported!');
    }

    logger$5.log(`HTML5 fullscreen integration: ${integration.type}.`);
    this.element = element;
    this.stateChangedCallback = stateChangedCallback;
    this.integration = integration;
    this.supportsOrientationLock = typeof ((_window$screen$orient = window.screen.orientation) == null ? void 0 : _window$screen$orient.type) === 'string';
    this.addDestroyable(destroyableEventListener(document, integration.changeEvent, this.checkIfActive.bind(this)));
    this.checkIfActive();
  }

  static isSupported() {
    return getSupportedIntegration() !== undefined;
  }

  getFullscreenState() {
    return {
      isSupported: true,
      isAvailable: true,
      isActive: this.isActive,
      isNativeUI: false
    };
  }

  enterFullscreen(options) {
    Promise.resolve(this.integration.requestFullscreen(this.element)).then(() => this.attemptOrientationLock(options == null ? void 0 : options.orientation)).catch(e => {
      logger$5.warn('Fullscreen request rejected:', e);
    });
  }

  exitFullscreen() {
    Promise.resolve(this.integration.exitFullscreen()).catch(e => {
      logger$5.warn('Failed to exit fullscreen:', e);
    });
  }

  checkIfActive() {
    const isActive = this.integration.getActiveElement() === this.element;

    if (isActive !== this.isActive) {
      this.isActive = isActive;
      this.stateChangedCallback('isActive');
    }
  }

  async attemptOrientationLock(orientation) {
    if (!this.supportsOrientationLock || !orientation) {
      return;
    }

    logger$5.log(`Attempting to lock screen orientation to '${orientation}'.`);
    return window.screen.orientation.lock(orientation).catch(e => {
      const error = toError(e);

      if (error.name === 'NotSupportedError') {
        logger$5.log('Screen orientation lock is not actually available.');
        this.supportsOrientationLock = false;
      } else {
        // - AbortError: Happens when the lock promise is cut off because one
        //   already exists or unlock is called before the lock promise had
        //   time to resolve.
        // - SecurityError: Can happen if the lock was not triggered by user
        //   action or if not in fullscreen.
        logger$5.log(`Orientation error: ${error.name.replace('Error', '')}. ${error.message}`);
      }
    });
  }

}

const logger$4 = getLogger('NativeRemotePlayback');
class NativeRemotePlayback extends DestroyableSet {
  constructor(media, stateChangedCallback) {
    super();
    this.remote = void 0;
    this.stateChangedCallback = void 0;
    this.scannerId = void 0;
    this.scannerTimeout = void 0;
    this.isScanningSupported = true;
    this.hasMedia = false;
    this.hasTargets = false;

    if (!media.remote) {
      throw new Error('RemotePlayback not supported!');
    }

    this.remote = media.remote;
    this.stateChangedCallback = stateChangedCallback;
    const isActiveChanged = this.stateChangedCallback.bind(this, 'isActive');
    this.addDestroyables([destroyableEventListener(this.remote, 'connect', isActiveChanged), destroyableEventListener(this.remote, 'connecting', isActiveChanged), destroyableEventListener(this.remote, 'disconnect', isActiveChanged), destroyableEventListener(this.remote, 'loadstart', () => {
      this.hasMedia = true;

      if (this.hasTargets) {
        this.stateChangedCallback('isAvailable');
      }
    }), destroyableEventListener(this.remote, 'emptied', () => {
      this.hasMedia = false;

      if (this.hasTargets) {
        this.stateChangedCallback('isAvailable');
      }
    })]);
    this.addEventListener('destroy', () => this.stopRemoteTargetScan());
  }

  static isSupported(media) {
    // Shortcut: media.disableRemotePlayback is dynamic, and should affect
    // availability, not support.
    return 'remote' in media && !media.disableRemotePlayback;
  }

  getRemotePlaybackState() {
    return {
      isSupported: true,
      isAvailable: this.hasMedia && this.hasTargets,
      isActive: this.remote.state === 'connected' || this.remote.state === 'connecting',
      isScanning: this.scannerId !== undefined
    };
  }

  startRemoteTargetScan(options) {
    if (this.isScanningSupported && this.scannerId === undefined) {
      this.scannerId = Number.NaN;
      this.remote.watchAvailability(hasTargets => {
        logger$4.log('Scan result:', hasTargets ? 'available' : 'not available');

        if (this.hasTargets !== hasTargets) {
          this.hasTargets = hasTargets;

          if (this.hasMedia) {
            this.stateChangedCallback('isAvailable');
          }

          if (hasTargets && options != null && options.stopWhenAvailable) {
            logger$4.log(`Stopping scan because targets.`);
            this.stopRemoteTargetScan();
          }
        }
      }).then(scannerId => {
        if (this.scannerId === undefined) {
          logger$4.log('Scanning cancelled before starting.');
          this.remote.cancelWatchAvailability(scannerId).catch(e => {
            logger$4.warn('Failed to stop scanning:', e);
          });
        } else {
          this.scannerId = scannerId;
          this.stateChangedCallback('isScanning');

          if (options != null && options.timeout) {
            logger$4.log(`Scanning for max ${options.timeout} ms.`);
            this.scannerTimeout = destroyableTimeout(() => this.stopRemoteTargetScan(), options.timeout);
          }
        }
      }).catch(() => {
        logger$4.log('Scanning is not supported.');
        this.isScanningSupported = false;
        this.scannerId = undefined; // Discovery of remote playback devices will happen only after prompt() is called.

        this.hasTargets = true;

        if (this.hasMedia) {
          this.stateChangedCallback('isAvailable');
        }
      });
    }
  }

  stopRemoteTargetScan() {
    if (this.scannerTimeout !== undefined) {
      this.scannerTimeout.destroy();
      this.scannerTimeout = undefined;
    }

    if (this.scannerId !== undefined) {
      if (!Number.isNaN(this.scannerId)) {
        this.remote.cancelWatchAvailability(this.scannerId).catch(e => {
          logger$4.warn('Failed to stop scanning:', e);
        });
      }

      this.scannerId = undefined;
      this.stateChangedCallback('isScanning');
    }
  }

  enterRemotePlayback() {
    this.remote.prompt().catch(e => {
      logger$4.warn('Remote playback prompt rejected:', e);
    });
  }

}

const logger$3 = getLogger('PiP');
class PiP extends DestroyableSet {
  constructor(video, stateChangedCallback) {
    super();
    this.video = void 0;
    this.stateChangedCallback = void 0;
    this.isAvailable = false;
    this.video = video;
    this.stateChangedCallback = stateChangedCallback;
    this.addDestroyables([destroyableEventListener(video, 'enterpictureinpicture', stateChangedCallback.bind(stateChangedCallback, 'isActive')), destroyableEventListener(video, 'leavepictureinpicture', stateChangedCallback.bind(stateChangedCallback, 'isActive')), destroyableEventListener(video, 'loadedmetadata', this.checkIfAvailable.bind(this)), destroyableEventListener(video, 'emptied', this.checkIfAvailable.bind(this))]);
    this.checkIfAvailable();
  }

  static isSupported() {
    return 'pictureInPictureEnabled' in document;
  }

  getPiPState() {
    return {
      isSupported: true,
      isAvailable: this.isAvailable,
      isActive: this.isActive()
    };
  }

  enterPiP() {
    var _this$video$requestPi, _this$video;

    Promise.resolve((_this$video$requestPi = (_this$video = this.video).requestPictureInPicture) == null ? void 0 : _this$video$requestPi.call(_this$video)).catch(e => {
      logger$3.warn('PiP request rejected:', e);
    });
  }

  exitPiP() {
    Promise.resolve(document.exitPictureInPicture == null ? void 0 : document.exitPictureInPicture()).catch(e => {
      logger$3.warn('Failed to exit PiP:', e);
    });
  }

  isActive() {
    return document.pictureInPictureElement === this.video;
  }

  checkIfAvailable() {
    const isAvailable = !!document.pictureInPictureEnabled && !this.video.disablePictureInPicture && this.video.readyState !== 0;

    if (this.isAvailable !== isAvailable) {
      this.isAvailable = isAvailable;
      this.stateChangedCallback('isAvailable');
    }
  }

}

const logger$2 = getLogger('AirPlay');
class WebkitAirPlay extends DestroyableSet {
  static isSupported() {
    return !!window.WebKitPlaybackTargetAvailabilityEvent;
  }

  constructor(media, stateChangedCallback) {
    super();
    this.media = void 0;
    this.stateChangedCallback = void 0;
    this.scannerHooks = void 0;
    this.hasMedia = false;
    this.hasTargets = false;
    this.isActive = false;
    this.media = media;
    this.stateChangedCallback = stateChangedCallback;
    this.addDestroyables([destroyableEventListener(media, 'webkitcurrentplaybacktargetiswirelesschanged', this.checkIfActive.bind(this)), destroyableEventListener(media, 'loadstart', this.checkIfHasMedia.bind(this)), destroyableEventListener(media, 'emptied', this.checkIfHasMedia.bind(this))]);
    this.addEventListener('destroy', () => this.stopRemoteTargetScan());
    this.checkIfActive();
  }

  getRemotePlaybackState() {
    return {
      isSupported: true,
      isAvailable: this.hasMedia && this.hasTargets,
      isActive: this.isActive,
      isScanning: this.scannerHooks !== undefined
    };
  }

  startRemoteTargetScan(options) {
    if (this.scannerHooks === undefined) {
      this.scannerHooks = new DestroyableSet();
      this.scannerHooks.addDestroyable(destroyableEventListener(this.media, 'webkitplaybacktargetavailabilitychanged', event => {
        const availability = event.availability;
        logger$2.log('Scan result:', availability);
        const hasTargets = availability === 'available';

        if (this.hasTargets !== hasTargets) {
          this.hasTargets = hasTargets;

          if (this.hasMedia) {
            this.stateChangedCallback('isAvailable');
          }
        }

        if (hasTargets && options != null && options.stopWhenAvailable) {
          logger$2.log(`Stopping scan because targets.`);
          this.stopRemoteTargetScan();
        }
      }));
      this.stateChangedCallback('isScanning');

      if (options != null && options.timeout) {
        logger$2.log(`Scanning for max ${options.timeout} ms.`);
        this.scannerHooks.addDestroyable(destroyableTimeout(this.stopRemoteTargetScan.bind(this), options.timeout));
      }
    }
  }

  stopRemoteTargetScan() {
    if (this.scannerHooks !== undefined) {
      this.scannerHooks.destroy();
      this.scannerHooks = undefined;
      this.stateChangedCallback('isScanning');
    }
  }

  enterRemotePlayback() {
    var _this$media$webkitSho, _this$media;

    (_this$media$webkitSho = (_this$media = this.media).webkitShowPlaybackTargetPicker) == null ? void 0 : _this$media$webkitSho.call(_this$media);
  }

  checkIfHasMedia() {
    const hasMedia = !this.isActive && !this.media.webkitWirelessVideoPlaybackDisabled;

    if (this.hasMedia !== hasMedia) {
      this.hasMedia = hasMedia;

      if (this.hasTargets) {
        this.stateChangedCallback('isAvailable');
      }
    }
  }

  checkIfActive() {
    if (this.media.webkitCurrentPlaybackTargetIsWireless !== this.isActive) {
      this.isActive = !this.isActive;
      this.stateChangedCallback('isActive');
    }

    this.checkIfHasMedia();
  }

}

/**
 * Collection of all the display mode APIs.
 */

/**
 * Standard fullscreen is preferred over WebKit video fullscreen if both are
 * supported.
 */

/**
 * Standard PiP is preferred over WebKit PiP if both are supported.
 */

/**
 * Support for the W3C Remote Playback API is disabled because of poor browser
 * support. See {@link DisplayModesOptions}.
 *
 * Scanning is required to make the remote playback API available.
 */
let WebkitPresentationMode;

(function (WebkitPresentationMode) {
  WebkitPresentationMode["Inline"] = "inline";
  WebkitPresentationMode["Fullscreen"] = "fullscreen";
  WebkitPresentationMode["PiP"] = "picture-in-picture";
})(WebkitPresentationMode || (WebkitPresentationMode = {}));

class WebkitPiP extends DestroyableSet {
  constructor(video, stateChangedCallback) {
    super();
    this.video = void 0;
    this.stateChangedCallback = void 0;
    this.isAvailable = false;
    this.isActive = false;
    this.video = video;
    this.stateChangedCallback = stateChangedCallback; // The resize event is used for detecting transitions between WebKit PiP and
    // WebKit video fullscreen, triggered in native UI (try iPad).

    const checkIfActive = this.checkIfActive.bind(this);
    const checkIfAvailable = this.checkIfAvailable.bind(this);
    this.addDestroyables([destroyableEventListener(video, 'webkitbeginfullscreen', checkIfActive), destroyableEventListener(video, 'webkitendfullscreen', checkIfActive), destroyableEventListener(video, 'resize', checkIfActive), destroyableEventListener(video, 'loadedmetadata', checkIfAvailable), destroyableEventListener(video, 'emptied', checkIfAvailable)]);
    this.checkIfActive();
  }

  static isSupported(video) {
    // Can't use video.webkitSupportsPresentationMode('picture-in-picture')
    // until the video element is initialized. iPhone/iPod doesn't support PiP.
    return typeof video.webkitSupportsPresentationMode === 'function' && !/iPhone|iPod/.exec(window.navigator.userAgent);
  }

  getPiPState() {
    return {
      isSupported: true,
      isAvailable: this.isAvailable,
      isActive: this.isActive
    };
  }

  enterPiP() {
    var _this$video$webkitSet, _this$video;

    (_this$video$webkitSet = (_this$video = this.video).webkitSetPresentationMode) == null ? void 0 : _this$video$webkitSet.call(_this$video, WebkitPresentationMode.PiP);
  }

  exitPiP() {
    var _this$video$webkitSet2, _this$video2;

    (_this$video$webkitSet2 = (_this$video2 = this.video).webkitSetPresentationMode) == null ? void 0 : _this$video$webkitSet2.call(_this$video2, WebkitPresentationMode.Inline);
  }

  checkIfAvailable() {
    const isAvailable = // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.video.webkitSupportsPresentationMode(WebkitPresentationMode.PiP) && (this.video.webkitPresentationMode === WebkitPresentationMode.Inline || this.video.webkitPresentationMode === WebkitPresentationMode.PiP);

    if (this.isAvailable !== isAvailable) {
      this.isAvailable = isAvailable;
      this.stateChangedCallback('isAvailable');
    }
  }

  checkIfActive() {
    const isActive = this.video.webkitPresentationMode === WebkitPresentationMode.PiP;

    if (this.isActive !== isActive) {
      this.isActive = isActive;
      this.stateChangedCallback('isActive');
    } // If webkitPresentationMode changes to something else than inline or
    // picture-in-picture, then disable PiP.


    this.checkIfAvailable();
  }

}

class WebkitVideoFullscreen extends DestroyableSet {
  constructor(video, stateChangedCallback) {
    super();
    this.video = void 0;
    this.stateChangedCallback = void 0;
    this.isAvailable = false;
    this.isActive = false;
    this.video = video;
    this.stateChangedCallback = stateChangedCallback; // The resize event is used for detecting transitions between WebKit PiP and
    // WebKit video fullscreen, triggered in native UI (try iPad).

    const checkIfActive = this.checkIfActive.bind(this);
    const checkIfAvailable = this.checkIfAvailable.bind(this);
    this.addDestroyables([destroyableEventListener(video, 'webkitfullscreenchange', checkIfActive), destroyableEventListener(video, 'webkitbeginfullscreen', checkIfActive), destroyableEventListener(video, 'webkitendfullscreen', checkIfActive), destroyableEventListener(video, 'resize', checkIfActive), destroyableEventListener(video, 'loadedmetadata', checkIfAvailable), destroyableEventListener(video, 'emptied', checkIfAvailable)]);
    this.checkIfActive();
  }

  static isSupported(video) {
    return typeof video.webkitEnterFullscreen === 'function' || typeof video.webkitEnterFullScreen === 'function';
  }

  getFullscreenState() {
    return {
      isSupported: true,
      isAvailable: this.isAvailable,
      isActive: this.isActive,
      isNativeUI: true
    };
  }

  enterFullscreen() {
    var _this$video$webkitEnt, _this$video;

    this.video.webkitEnterFullscreen ? this.video.webkitEnterFullscreen() : (_this$video$webkitEnt = (_this$video = this.video).webkitEnterFullScreen) == null ? void 0 : _this$video$webkitEnt.call(_this$video);
  }

  exitFullscreen() {
    var _this$video$webkitExi, _this$video2;

    this.video.webkitExitFullscreen ? this.video.webkitExitFullscreen() : (_this$video$webkitExi = (_this$video2 = this.video).webkitExitFullScreen) == null ? void 0 : _this$video$webkitExi.call(_this$video2);
  }

  checkIfAvailable() {
    const isAvailable = !!this.video.webkitSupportsFullscreen && (!this.video.webkitPresentationMode || this.video.webkitPresentationMode === WebkitPresentationMode.Inline || this.video.webkitPresentationMode === WebkitPresentationMode.Fullscreen);

    if (this.isAvailable !== isAvailable) {
      this.isAvailable = isAvailable;
      this.stateChangedCallback('isAvailable');
    }
  }

  checkIfActive() {
    // PresentationMode is fullscreen both when entering _and_ exiting
    // fullscreen mode, so we need to check webkitDisplayingFullscreen too,
    // since that is false/true depending.
    const isActive = this.video.webkitPresentationMode ? this.video.webkitPresentationMode === WebkitPresentationMode.Fullscreen && !this.video.webkitDisplayingFullscreen : !!this.video.webkitDisplayingFullscreen;

    if (this.isActive !== isActive) {
      this.isActive = isActive;
      this.stateChangedCallback('isActive');
    } // If webkitPresentationMode changes to something else than inline or
    // fullscreen, then disable fullscreen.


    this.checkIfAvailable();
  }

}

const logger$1 = getLogger('HTMLDisplayModes');
class LocalDisplayModes {
  constructor(eventEmitter, containerElement, mediaElement, options) {
    this.eventEmitter = void 0;
    this.fullscreen = void 0;
    this.pip = void 0;
    this.remotePlayback = void 0;
    this.eventEmitter = eventEmitter;

    if (Fullscreen.isSupported()) {
      this.fullscreen = new Fullscreen(containerElement, this.handleFullscreenStateChanged.bind(this));
      logger$1.log('Fullscreen is supported.');
    }

    if (mediaElement instanceof HTMLVideoElement) {
      if (!this.fullscreen && WebkitVideoFullscreen.isSupported(mediaElement)) {
        this.fullscreen = new WebkitVideoFullscreen(mediaElement, this.handleFullscreenStateChanged.bind(this));
        logger$1.log('WebKit video fullscreen is supported');
      }

      if (PiP.isSupported()) {
        this.pip = new PiP(mediaElement, this.handlePiPStateChanged.bind(this));
        logger$1.log('PiP is supported.');
      } else if (WebkitPiP.isSupported(mediaElement)) {
        this.pip = new WebkitPiP(mediaElement, this.handlePiPStateChanged.bind(this));
        logger$1.log('WebKit PiP is supported.');
      }
    }

    if (mediaElement) {
      if (options != null && options.enableNativeRemotePlayback && NativeRemotePlayback.isSupported(mediaElement)) {
        this.remotePlayback = new NativeRemotePlayback(mediaElement, this.handleRemotePlaybackStateChanged.bind(this));
        logger$1.log('RemotePlayback is supported.');
      } else if (WebkitAirPlay.isSupported()) {
        this.remotePlayback = new WebkitAirPlay(mediaElement, this.handleRemotePlaybackStateChanged.bind(this));
        logger$1.log('WebKit AirPlay is supported.');
      }
    }
  }

  getFullscreenState() {
    var _this$fullscreen$getF, _this$fullscreen;

    return (_this$fullscreen$getF = (_this$fullscreen = this.fullscreen) == null ? void 0 : _this$fullscreen.getFullscreenState()) != null ? _this$fullscreen$getF : {
      isSupported: false,
      isAvailable: false,
      isActive: false,
      isNativeUI: false
    };
  }

  enterFullscreen(options) {
    var _this$pip, _this$fullscreen2;

    if ((_this$pip = this.pip) != null && _this$pip.getPiPState().isActive) {
      this.pip.exitPiP();
    }

    (_this$fullscreen2 = this.fullscreen) == null ? void 0 : _this$fullscreen2.enterFullscreen(options);
  }

  exitFullscreen() {
    var _this$fullscreen3;

    (_this$fullscreen3 = this.fullscreen) == null ? void 0 : _this$fullscreen3.exitFullscreen();
  }

  getPiPState() {
    var _this$pip$getPiPState, _this$pip2;

    return (_this$pip$getPiPState = (_this$pip2 = this.pip) == null ? void 0 : _this$pip2.getPiPState()) != null ? _this$pip$getPiPState : {
      isSupported: false,
      isAvailable: false,
      isActive: false
    };
  }

  enterPiP() {
    var _this$fullscreen4, _this$pip3;

    if ((_this$fullscreen4 = this.fullscreen) != null && _this$fullscreen4.getFullscreenState().isActive) {
      this.fullscreen.exitFullscreen();
    }

    (_this$pip3 = this.pip) == null ? void 0 : _this$pip3.enterPiP();
  }

  exitPiP() {
    var _this$pip4;

    (_this$pip4 = this.pip) == null ? void 0 : _this$pip4.exitPiP();
  }

  getRemotePlaybackState() {
    var _this$remotePlayback$, _this$remotePlayback;

    return (_this$remotePlayback$ = (_this$remotePlayback = this.remotePlayback) == null ? void 0 : _this$remotePlayback.getRemotePlaybackState()) != null ? _this$remotePlayback$ : {
      isSupported: false,
      isAvailable: false,
      isActive: false,
      isScanning: false
    };
  }

  startRemoteTargetScan(options) {
    var _this$remotePlayback2;

    (_this$remotePlayback2 = this.remotePlayback) == null ? void 0 : _this$remotePlayback2.startRemoteTargetScan(options);
  }

  stopRemoteTargetScan() {
    var _this$remotePlayback3;

    (_this$remotePlayback3 = this.remotePlayback) == null ? void 0 : _this$remotePlayback3.stopRemoteTargetScan();
  }

  enterRemotePlayback() {
    var _this$remotePlayback4;

    (_this$remotePlayback4 = this.remotePlayback) == null ? void 0 : _this$remotePlayback4.enterRemotePlayback();
  }

  destroy() {
    var _this$fullscreen5, _this$pip5, _this$remotePlayback5;

    (_this$fullscreen5 = this.fullscreen) == null ? void 0 : _this$fullscreen5.destroy();
    this.fullscreen = undefined;
    (_this$pip5 = this.pip) == null ? void 0 : _this$pip5.destroy();
    this.pip = undefined;
    (_this$remotePlayback5 = this.remotePlayback) == null ? void 0 : _this$remotePlayback5.destroy();
    this.remotePlayback = undefined;
  }

  handleFullscreenStateChanged(field) {
    if (this.fullscreen) {
      const state = this.fullscreen.getFullscreenState();
      logger$1.log(`Fullscreen: ${field} is ${state[field]}.`);
      this.eventEmitter.emit(CoreEvent.FullscreenStateChange, {
        state,
        field
      });
    }
  }

  handlePiPStateChanged(field) {
    if (this.pip) {
      const state = this.pip.getPiPState();
      logger$1.log(`PiP: ${field} is ${state[field]}.`);
      this.eventEmitter.emit(CoreEvent.PiPStateChange, {
        state,
        field
      });
    }
  }

  handleRemotePlaybackStateChanged(field) {
    if (this.remotePlayback) {
      const state = this.remotePlayback.getRemotePlaybackState();
      logger$1.log(`RemotePlayback: ${field} is ${state[field]}.`);
      this.eventEmitter.emit(CoreEvent.RemotePlaybackStateChange, {
        state,
        field
      });
    }
  }

}

/**
 * Keep track of the current quality level of an adaptive bitrate stream. The
 * setCurrentQualityBy* methods require that the available qualities are set
 * first, for the lookups to work.
 */
class QualityTracker {
  constructor(emitter) {
    this.emitter = void 0;
    this.availableQualities = [];
    this.currentQuality = undefined;
    this.emitter = emitter;
  }

  setAvailableQualities(qualities) {
    this.availableQualities = qualities;
  }

  setCurrentQualityByLevel(level) {
    const quality = this.availableQualities.find(q => q.level === level);

    if (quality) {
      this.setCurrentQuality(quality);
    }
  }

  setCurrentQualityByBitrate(bitrate) {
    const quality = this.availableQualities.find(q => q.bitrate === bitrate || q.averageBitrate === bitrate);

    if (quality) {
      this.setCurrentQuality(quality);
    }
  }

  setCurrentQualityByVideoProps(width, height, frameRate) {
    const quality = this.availableQualities.find(q => q.width === width && q.height === height && (frameRate === undefined || q.frameRate === frameRate));

    if (quality) {
      this.setCurrentQuality(quality);
    }
  }

  setCurrentQuality(quality) {
    if (this.currentQuality !== quality) {
      this.currentQuality = quality;
      this.dispatchQualityChangeEvent();
    }
  }

  getCurrentQuality() {
    return this.currentQuality;
  }
  /**
   * For internal use, except in special cases where it makes sense to
   * re-announce the current quality level (read: replay);
   */


  dispatchQualityChangeEvent() {
    if (this.currentQuality) {
      this.emitter.emit(CoreEvent.QualityChange, this.currentQuality);
    }
  }

}

function getMediaElement(providedElement, options) {
  var _options$type;

  const mediaElement = resolveMediaElement(providedElement, (_options$type = options == null ? void 0 : options.type) != null ? _options$type : 'video');
  applyMediaElementOptions(mediaElement, options);
  return mediaElement;
}

function resolveMediaElement(providedElement, type) {
  // Use any provided media element, even if the type doesn't match.
  if (providedElement instanceof HTMLMediaElement) {
    return providedElement;
  } // Query for an existing media element inside the provided container.


  const existingElement = providedElement.querySelector(type);

  if (existingElement) {
    return existingElement;
  } // Create a new media element and add it to the beginning of the provided
  // container.


  const newElement = document.createElement(type);
  providedElement.insertBefore(newElement, providedElement.firstChild);
  return newElement;
}

function applyMediaElementOptions(mediaElement, options) {
  if ((options == null ? void 0 : options.crossOrigin) === 'none') {
    mediaElement.removeAttribute('crossorigin');
  } else {
    var _options$crossOrigin;

    mediaElement.crossOrigin = (_options$crossOrigin = options == null ? void 0 : options.crossOrigin) != null ? _options$crossOrigin : 'anonymous';
  }

  if ((options == null ? void 0 : options.playsInline) === false) {
    mediaElement.removeAttribute('playsinline');
    mediaElement.removeAttribute('webkit-playsinline');
  } else {
    mediaElement.setAttribute('playsinline', '');
    mediaElement.setAttribute('webkit-playsinline', '');
  }

  if (options != null && options.videoPoster && mediaElement instanceof HTMLVideoElement) {
    mediaElement.poster = options.videoPoster;
  }
}

function getObjectElement(providedElement, attrs) {
  const objectElement = resolveObjectElement(providedElement, attrs.type);
  Object.getOwnPropertyNames(attrs).forEach(key => {
    objectElement.setAttribute(key, `${attrs[key]}`);
  });
  return objectElement;
}

function resolveObjectElement(providedElement, type) {
  // Use any provided object element.
  if (providedElement instanceof HTMLObjectElement && providedElement.type === type) {
    return providedElement;
  } // Query for an existing object element inside the provided container.


  const existingElement = providedElement.querySelector(`object[type="${type}"]`);

  if (existingElement) {
    return existingElement;
  } // Create a new object element and add it to the beginning of the provided
  // container.


  const newElement = document.createElement('object');
  newElement.type = type;
  providedElement.insertBefore(newElement, providedElement.firstChild);
  return newElement;
}

const logger = getLogger('PlayerCore');
/**
 * Provide Player Core with different elements per task, instead of one element
 * for all tasks.
 */

class PlayerCore {
  /**
   * @param playerElement The HTML element which should emit custom events, go
   * fullscreen and contain the media element, or an object specifying each of
   * those elements.
   *
   * @param configuredAdapters A prioritized list of adapters and their
   * configurations to select from when creating new sessions. Note: Difference
   * devices might have different preferences.
   *
   * @param options
   */
  constructor(playerElement, configuredAdapters, options) {
    this.playerElement = void 0;
    this.configuredAdapters = void 0;
    this.options = void 0;
    this.playerElement = playerElement;
    this.configuredAdapters = configuredAdapters;
    this.options = options;
  }

  canPlayType(mediaType) {
    return this.findSupportedAdapter(mediaType) !== undefined;
  }

  createPlaybackSession(mediaUrl, mediaType, initialSessionState) {
    var _this$options2, _this$options3, _this$options4, _this$options4$liveOp;

    const resolvedMediaType = this.resolveMediaType(mediaUrl, mediaType);

    if (resolvedMediaType === undefined) {
      throw new Error('Unable to detect media type. Please specify.');
    }

    const configuredAdapter = this.findSupportedAdapter(resolvedMediaType);

    if (configuredAdapter === undefined) {
      throw new Error(`No matching adapter for ${toMediaTypeString(resolvedMediaType)}.`);
    }

    const adapterClass = configuredAdapter.adapterClass;
    logger.log(`Using adapter:`, adapterClass.getAdapterInfo()); // Wire everything up!
    // Event emitters.

    const eventElement = this.playerElement instanceof HTMLElement ? this.playerElement : this.playerElement.eventElement;
    const externalEventEmitter = new CoreEventEmitter();
    const customEventEmitter = new CoreCustomEventEmitter(eventElement);
    const eventEmitter = new MultiEmitEventEmitter([externalEventEmitter, customEventEmitter]); // Quality tracker.

    const qualityTracker = new QualityTracker(eventEmitter); // Media element.

    const providedElement = this.playerElement instanceof HTMLElement ? this.playerElement : this.playerElement.mediaElement;
    let mediaElement;

    if (adapterClass.getAdapterInfo().id === 'AVPlay') {
      mediaElement = getObjectElement(providedElement, {
        type: 'application/avplayer'
      });
    } else {
      var _this$options;

      const mediaElementOptions = {
        type: this.resolvePlayerType(resolvedMediaType),
        ...((_this$options = this.options) == null ? void 0 : _this$options.mediaElementOptions),
        ...initialSessionState // Override videoPoster, if set.

      };
      mediaElement = getMediaElement(providedElement, mediaElementOptions);
    } // Display modes.


    const fullscreenElement = this.playerElement instanceof HTMLElement ? this.playerElement : this.playerElement.fullscreenElement;
    const displayModes = new LocalDisplayModes(eventEmitter, fullscreenElement, mediaElement instanceof HTMLMediaElement ? mediaElement : undefined, (_this$options2 = this.options) == null ? void 0 : _this$options2.displayModesOptions); // Adapter (this context fits the adapters in the adapters folder).

    const adapterContext = {
      mediaElement,
      eventEmitter,
      qualityTracker,
      media: {
        url: mediaUrl,
        mediaType: resolvedMediaType
      },
      initialPlaybackState: initialSessionState,
      liveOptions: (_this$options3 = this.options) == null ? void 0 : _this$options3.liveOptions
    }; // eslint-disable-next-line @typescript-eslint/no-unsafe-argument

    const adapter = new adapterClass(adapterContext, ...configuredAdapter.adapterConfig); // Session.

    const session = new PlaybackSession(adapter, eventEmitter, externalEventEmitter, qualityTracker, displayModes); // Scrubber update events.

    new ScrubberUpdateEvents(session, eventEmitter, (_this$options4 = this.options) == null ? void 0 : (_this$options4$liveOp = _this$options4.liveOptions) == null ? void 0 : _this$options4$liveOp.liveScrubberUpdateInterval);
    return session;
  }

  resolveMediaType(mediaUrl, mediaType) {
    return mediaType !== undefined ? typeof mediaType === 'string' ? parseMediaTypeString(mediaType) : mediaType : guessMediaTypeOfUrl(mediaUrl);
  }

  resolvePlayerType(mediaType) {
    if (mediaType) {
      return isAudioOnlyMediaType(mediaType) ? 'audio' : 'video';
    }

    return 'video';
  } // eslint-disable-next-line @typescript-eslint/no-explicit-any


  findSupportedAdapter(mediaType) {
    return this.configuredAdapters.find(adapter => adapter.adapterClass.canPlayType(mediaType));
  }

}

export { CoreCustomEventEmitter as C, MultiEmitEventEmitter as M, NullDisplayModes as N, PlaybackSession as P, QualityTracker as Q, ScrubberUpdateEvents as S, CoreEventEmitter as a, ConfiguredAdapter as b, PlayerCore as c };
